import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './about.css';

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Set visibility state
    setIsVisible(true);
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Set document title
    document.title = "About Us | Construction Management System";
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const companyValues = [
    {
      title: "Excellence",
      description: "We strive for excellence in every project we undertake, ensuring quality at every step.",
      icon: "🏆"
    },
    {
      title: "Integrity",
      description: "We maintain transparency and honesty in all our dealings with clients and partners.",
      icon: "🤝"
    },
    {
      title: "Innovation",
      description: "We embrace new technologies and methodologies to improve construction processes.",
      icon: "💡"
    },
    {
      title: "Safety",
      description: "We prioritize the safety of our workers and ensure all projects follow strict safety guidelines.",
      icon: "🛡️"
    }
  ];

  const goals = [
    "Become the leading construction management solution provider in India by 2030",
    "Expand operations to international markets within the next 5 years",
    "Reduce construction waste by implementing sustainable practices",
    "Continuously improve our technology to enhance project efficiency"
  ];

  return (
    <div className="about-us-container">
      {/* Hero Section */}
      <motion.div 
        className="hero-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <h1>Building Tomorrow's Infrastructure Today</h1>
          <p>Your trusted partner in construction management excellence since 2024</p>
        </div>
      </motion.div>

      {/* About Section */}
      <motion.section 
        className="about-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={staggerChildren}
      >
        <motion.div className="section-title" variants={fadeIn}>
          <h2>About Our Company</h2>
          <div className="underline"></div>
        </motion.div>
        
        <motion.div className="about-content" variants={fadeIn}>
          <div className="about-image">
            <img src="https://visibuild.com/uploads/2024/04/Construction-Site-Manager.jpg" alt="Construction Management Team" />
          </div>
          <div className="about-text">
            <h3>Who We Are</h3>
            <p>
              Founded in 2024, our construction management system is revolutionizing how construction projects are planned, executed, and monitored. Based in Pune, we combine cutting-edge technology with industry expertise to deliver comprehensive solutions for construction professionals.
            </p>
            <p>
              Our platform helps identify critical issues, streamline approvals, and track project completion rates to ensure your projects stay on schedule and within budget.
            </p>
          </div>
        </motion.div>
      </motion.section>

      {/* Founder Section */}
      <motion.section 
        className="founder-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ delay: 0.2 }}
      >
        <div className="founder-content">
          <div className="founder-image">
            <img src="https://media.istockphoto.com/id/1179632002/photo/senior-businessman-architect-helmet-construction-site-building-architecture.jpg?s=612x612&w=0&k=20&c=ix9Yg5GTkagv0JUru72-liMn-BZBjR85pPVEkRpAWyc=" alt="Company Founder" />
          </div>
          <div className="founder-text">
            <h2>Our Founder</h2>
            <h3>John Doe</h3>
            <p className="founder-title">Chief Executive Officer</p>
            <p className="founder-bio">
              With over 15 years of experience in the construction industry and software development, John founded our company with a vision to bridge the gap between traditional construction practices and modern technology. His innovative approach has helped numerous construction companies optimize their operations and achieve greater success.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Values Section */}
      <motion.section 
        className="values-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={staggerChildren}
      >
        <motion.div className="section-title" variants={fadeIn}>
          <h2>Our Core Values</h2>
          <div className="underline"></div>
        </motion.div>
        
        <motion.div className="values-grid" variants={staggerChildren}>
          {companyValues.map((value, index) => (
            <motion.div 
              className="value-card" 
              key={index}
              variants={fadeIn}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="value-icon">{value.icon}</div>
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* Goals Section */}
      <motion.section 
        className="goals-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ delay: 0.4 }}
      >
        <div className="section-title">
          <h2>Our Goals</h2>
          <div className="underline"></div>
        </div>
        
        <div className="goals-content">
          <div className="goals-image">
            <img src="https://teltonika-networks.com/cdn/extras/13407/org-iot-for-reducing-risks-in-construction-sites.webp" alt="Construction Site" />
          </div>
          <div className="goals-list">
            <ul>
              {goals.map((goal, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 * index }}
                >
                  {goal}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </motion.section>

      {/* Location Section */}
      <motion.section 
        className="location-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ delay: 0.6 }}
      >
        <div className="section-title">
          <h2>Our Location</h2>
          <div className="underline"></div>
        </div>
        
        <div className="location-content">
          <div className="location-text">
            <h3>Headquarters</h3>
            <p>123 Tech Park, Hinjewadi</p>
            <p>Pune, Maharashtra 411057</p>
            <p>India</p>
            <p className="contact-info">
              <strong>Email:</strong> info@constructionmanagementsystem.com<br />
              <strong>Phone:</strong> +91 98765 43210
            </p>
          </div>
          <div className="location-image">
            <img src="https://naiknavare.com/blog/wp-content/uploads/2020/11/seasons-day.jpg" alt="Office Location" />
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="cta-section"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={fadeIn}
        transition={{ delay: 0.8 }}
      >
        <div className="cta-content">
          <h2>Ready to Transform Your Construction Management?</h2>
          <p>Join the growing community of construction professionals using our platform</p>
          <button className="cta-button">Contact Us Today</button>
        </div>
      </motion.section>
    </div>
  );
};

export default AboutUs;